import { SimpleSpinner } from '@components/Loading/SimpleSpinner';
import { Toast } from '@components/Toast';
import type { TreeItemProps } from '@components/TreeView';
import TreeView from '@components/TreeView';
import { faListTree } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled } from '@mui/material';
import { type CategoryNode, type GetPartCategoryTreeResponse } from '@services/productInventory';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface PartCategoriesTreeProps {
  data: GetPartCategoryTreeResponse | void;
  isError?: boolean;
  isLoading?: boolean;
  titleTranslationKey: string;
  children?: React.ReactNode;
}

export function PartCategoriesTree({
  data,
  isError,
  isLoading,
  titleTranslationKey,
  children,
}: Readonly<PartCategoriesTreeProps>) {
  const { t } = useTranslation('manageProduct');

  const formattedCategories = useMemo<TreeItemProps[] | undefined>(() => {
    if (data?.categories == null) {
      return undefined;
    }
    /* istanbul ignore next */
    return [formatCategory(data?.categories)];
  }, [data]);

  if (isLoading) {
    return (
      <SpinnerBox>
        <SimpleSpinner data-testid="simple-spinner" />
      </SpinnerBox>
    );
  }

  return (
    <>
      <TreeBox>
        <TreeTitleBox>
          <FontAwesomeIcon icon={faListTree} size="lg" />
          <TreeTitle data-testid="tree-title">
            <Trans i18nKey={titleTranslationKey} />
          </TreeTitle>
          {children}
        </TreeTitleBox>
        <TreeView
          data-testid="rich-categories-tree"
          items={formattedCategories ?? /* istanbul ignore next - not worth testing */ []}
          isBold
        />
      </TreeBox>
      <Toast
        dependency={isError}
        severity="error"
        title={t('partsCategories.currentPartsCategories.errorGettingPartsCategoriesTitle')}
        message={t('partsCategories.currentPartsCategories.errorGettingPartsCategoriesDescription')}
      />
    </>
  );
}
/* istanbul ignore next */
const formatCategory = (category: CategoryNode): TreeItemProps => ({
  key: category.name,
  title: category.name,
  children: category.children.map(formatCategory),
});

const TreeBox = styled('div')`
  padding: 4px;
  background-color: ${({ theme }) => theme.palette.grey['400']};
  justify-content: end;
  display: flex;
  flex-direction: column;
  border-radius: 0.2rem;
  min-height: 30vh;
  height: 100%;
  width: 100%;
`;

const SpinnerBox = styled('div')`
  padding: 4em;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const TreeTitle = styled('h3')`
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightRegular};
  margin-bottom: 0;
`;

const TreeTitleBox = styled('span')`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
  padding: 1rem 0.5rem;
  flex: 0;
`;
